<template>
	<div id="app" :class="vueAppClasses">
    <ion-app>
      <offline @detected-condition="handleConnectivityChange"></offline>
      <vs-row class="w-full update-available bg-warning" v-if="showUpgradeUI.active" vs-justify="center" vs-align="center">
        <vs-col class="text-center">
          We've finetuned application in the background. Please reload to activate new features. <vs-button type="filled" color="primary" @click="accept" size="small">Reload</vs-button>
        </vs-col>
      </vs-row>
      <router-view @setAppClasses="setAppClasses"></router-view>
      <vs-popup class="sync-popup" :active.sync="sync_popup">
        <h2 class="mt-1 text-center">
          <font-awesome-icon class="mr-2" :icon="cloud"/>
          Synchronize
        </h2>
        <h5 class="mt-4 mb-base text-center">You are back online, do you want to initiate sync for offline data now?</h5>
        <div class="text-center mb-3">
          <vs-button @click="() => {
            sync_popup=false
            this.$router.push({name: 'synchronization', query: {auto_sync: true}})
          }" color="primary" size="small" type="filled" class="mr-3">Sync</vs-button>
          <vs-button @click="sync_popup=false" size="small" color="danger" type="filled">Cancel</vs-button>
        </div>
      </vs-popup>
    </ion-app>
	</div>
</template>

<script>
import themeConfig from '@/../themeConfig.js'
import offline from 'v-offline';

import { Cache } from './utils/cache';
import { StatusBar } from '@ionic-native/status-bar'
import { faCloudUploadAlt } from '@fortawesome/free-solid-svg-icons';

export default {
  components: {
    offline,
  },
  data: () => {
    return  {
      vueAppClasses: [],
      showUpgradeUI: {
        active: false,
        data: null,
      },
      offline_data_sync: false,
      sync_popup: false,
    }
  },
  watch: {
    '$store.state.theme'(val) {
      this.toggleClassInBody(val);
    },
    '$store.state.auth.userRole'(val) {
      this.$acl.change(val)
    },
    '$vs.rtl' (val) {
      document.documentElement.setAttribute('dir', val ? 'rtl' : 'ltr')
    },
    '$route'() {
      let ion_content = document.querySelector('ion-content')
      if (ion_content) ion_content.scrollToTop()
    },
    '$store.state.is_online'(newV) {
      this.restart_sync_timer(newV)
    }
  },
  computed: {
    cloud: () => faCloudUploadAlt,
  },
  created() {
    const dir = this.$vs.rtl ? 'rtl' : 'ltr'
    document.documentElement.setAttribute('dir', dir)

    window.addEventListener('resize', this.handleWindowResize)
    window.addEventListener('scroll', this.handleScroll)

    window.addEventListener("swRegistered", (e) => {
      console.log(e)
      this.$store.dispatch('updateServiceWorker', e.detail.registration)
    })
    window.addEventListener("swUpdateAvailable", (e) => {
      this.showUpgradeUI.active = true
      this.showUpgradeUI.data = e.detail.worker
    })
    StatusBar.backgroundColorByHexString('#ffffff')
    StatusBar.styleDefault()

    // Check for version updates
    this.checkVersionUpdate()

    // run script for caching
    if (this.$store.state.is_mobile) {
      setTimeout(()=> {
        const cache = new Cache()
        this.$store.dispatch('offlineReady', -2)
        cache.run_cache().then(() => {
          this.$store.dispatch('offlineReady', true)
        }).catch(() => {
          this.$store.dispatch('offlineReady', -1)
        })
      }, 10000)
    }
  },
  methods: {
    checkVersionUpdate() {
      const currentVersion = this.$store.state.version
      const storedVersion = localStorage.getItem('app_version')
      
      if (storedVersion && storedVersion !== currentVersion) {
        // Version has changed, show update message
        this.showUpgradeUI.active = true
        this.showUpgradeUI.data = null
      }
      
      // Store current version for next comparison
      localStorage.setItem('app_version', currentVersion)
    },
    restart_sync_timer(is_online) {
      if (this.$store.state.is_mobile) {
        this.online_seconds = 0
        if(is_online) {
          this.online_interval = setInterval(() => {
            this.online_seconds += 1
            
            if (this.online_seconds == 30 && this.$store.state.organisation.organisation_user) {
              this.offline_data_sync = true
              this.sync_popup = true
              clearInterval(this.online_interval)
            }
          }, 1000)
  
        } else {
          this.sync_popup = false
          this.offline_data_sync = false
          if(this.online_interval) clearInterval(this.online_interval)
        }
      }
    },
    handleConnectivityChange(status){
      this.$store.dispatch("updateConnectivityStatus", status)
    },
    accept() {
      if (this.showUpgradeUI.data) {
        // Service worker update
        this.showUpgradeUI.data.postMessage({type: 'SKIP_WAITING'})
      } else {
        // Version update - just reload the page
        window.location.reload()
      }
    },
    toggleClassInBody(className) {
      if (className == 'dark') {
        if (document.body.className.match('theme-semi-dark')) document.body.classList.remove('theme-semi-dark');
        document.body.classList.add('theme-dark');
      } else if (className == 'semi-dark') {
        if (document.body.className.match('theme-dark')) document.body.classList.remove('theme-dark');
        document.body.classList.add('theme-semi-dark');
      } else {
        if (document.body.className.match('theme-dark')) document.body.classList.remove('theme-dark');
        if (document.body.className.match('theme-semi-dark')) document.body.classList.remove('theme-semi-dark');
      }
    },
    handleWindowResize(event) {
      this.$store.commit('UPDATE_WINDOW_WIDTH', window.innerWidth)

      // Set --vh property
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`)
    },
    setAppClasses (classesStr) {
      this.vueAppClasses.push(classesStr)
    },
    handleScroll () {
      // this.$store.commit('UPDATE_WINDOW_SCROLL_Y', window.scrollY)
    }
  },
  mounted() {
    this.toggleClassInBody(themeConfig.theme)
    this.$store.dispatch('updateWindowWidth', window.innerWidth)
    this.handleConnectivityChange(navigator.onLine)
    this.restart_sync_timer(navigator.onLine)

    const vh = window.innerHeight * 0.01
    // Then we set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty('--vh', `${vh}px`)

    // this.$nextTick(() => {
    //   window.addEventListener('resize', this.handleWindowResize);
    // });
  },
  destroyed() {
    window.removeEventListener('resize', this.handleWindowResize)
    window.removeEventListener('scroll', this.handleScroll)
  }
}

</script>

<style lang="scss">
.update-available {
  position: fixed;
  bottom: 0;
  height: 60px;
  color: #fff;
  font-weight: 700;
  z-index: 999999;
}
.vs-input--label{ font-weight: 600 !important; font-size: .95rem !important; line-height:32px !important;
  padding-left: 0 !important;
}
.vdp-datepicker__calendar {
  z-index: 500!important;
}
.icon-button {
  padding: 0.5rem !important;
}
.con-vs-tooltip {
  display: inline-block;
}
.vs-input--input, .vs-button, .vs-textarea,
.vs__dropdown-toggle {
  border-radius: 0!important;
}

.ion-page {
  contain: size style !important;

  overflow: auto !important;
  justify-content: flex-start !important;
}
body {
  background: #fff !important;
}
.vs-popup--content {
  overflow: unset !important;
}

.freeze_row {
  .vs-table--tbody {
    overflow-y: scroll;
    max-height: 600px;
  }
}

.freeze_row.row1 tr:nth-child(1) {
  position: sticky;
  opacity: 1 !important;
  z-index: 1000;
  top: 0;
}
.freeze_row.row2 tr:nth-child(2) {
  position: sticky;
  opacity: 1 !important;
  z-index: 1000;
  top: 0;
}

.freeze_row.row3 tr:nth-child(3) {
  position: sticky;
  opacity: 1 !important;
  z-index: 1000;
  top: 10.8%;
}

.freeze_row.row3_top_diff tr:nth-child(3) {
  position: sticky;
  opacity: 1 !important;
  z-index: 1000;
  top: 7.5%;
}

.sync-popup {
  .vs-popup--title {
    display: none;
  }
  .vs-popup--close {
    position: absolute;
    right: 0;
    top: 5px;
  }
}

// dashboard common styles
.fix-filters {
    position: sticky; 
    top: 10.5vh; 
    padding: 5px; 
    opacity: 1; 
    z-index: 2500;
    .vx-card {
      box-shadow: 0 0 10px #CDCDCD;
    }
}

@media screen and (min-width: 769px) and (max-width: 1300px) {
  .fix-filters {
    top: 8.5vh;
  }
}

@media screen and (min-width: 1300px){
  .fix-filters {
    top: 10.5vh;
  }
}

.txt-color {
  color: rgba(var(--vs-primary),1)!important; 
  font-weight: 600 !important;
}

.align-items-center {
  align-items: center;
}

.flex-center {
  display: flex;
  align-items: center;
  justify-content: center;
}

.vs-tabs-primary .activeChild button {
  color: rgba(var(--vs-primary), 1)!important;
  font-weight: 600;
}

.vs-tabs-primary button.vs-button-filled:not(:disabled):hover {
  color: rgba(255, 255, 255, 1)!important;
}
</style>
